import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";
import toast from "react-hot-toast";

import {
  createSurveyWebhook,
  deleteSurveyWebhook,
  listSurveyWebhookDeliveries,
  listSurveyWebhooks,
  messageFromError,
  replaySurveyWebhookDelivery,
  rotateSurveyWebhookSecret,
  testSurveyWebhook,
  updateSurveyWebhook,
} from "../../utils/api";
import {
  EmptyState,
  ErrorAlert,
  PlanLock,
  StatusBadge,
  UpgradeGrid,
  formatDate,
  labelize,
} from "../../layout/secure/common";

const EMPTY = {
  id: null,
  name: "",
  url: "",
  events: ["ferasurvey.response.submitted"],
  enabled: true,
};

function eventLabel(value) {
  return labelize(String(value || "").replace(/^ferasurvey\./, "").replace(/\./g, " "));
}

export default function WebhookSettings({ usage }) {
  const entitled = Boolean(usage?.features?.customerWebhooks);
  const [items, setItems] = useState(null);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(null);
  const [secret, setSecret] = useState("");
  const [deliveryWebhook, setDeliveryWebhook] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const result = await listSurveyWebhooks();
      setItems(result.items || []);
      setEvents(result.events || []);
    } catch (requestError) {
      setError(messageFromError(requestError, "Customer webhooks could not be loaded."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async event => {
    event.preventDefault();
    setBusy("save");
    setError("");
    try {
      const result = form.id
        ? await updateSurveyWebhook(form.id, form)
        : await createSurveyWebhook(form);
      setForm(null);
      if (result.secret) setSecret(result.secret);
      toast.success(form.id ? "Webhook updated." : "Webhook created.");
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The webhook could not be saved."));
    } finally {
      setBusy("");
    }
  };

  const toggle = async item => {
    setBusy(item.id);
    try {
      await updateSurveyWebhook(item.id, { enabled: !item.enabled });
      toast.success(item.enabled ? "Webhook disabled." : "Webhook enabled.");
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The webhook status could not be changed."));
    } finally {
      setBusy("");
    }
  };

  const remove = async item => {
    setConfirmation(null);
    setBusy(item.id);
    try {
      await deleteSurveyWebhook(item.id);
      toast.success("Webhook deleted.");
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The webhook could not be deleted."));
    } finally {
      setBusy("");
    }
  };

  const rotate = async item => {
    setConfirmation(null);
    setBusy(item.id);
    try {
      const result = await rotateSurveyWebhookSecret(item.id);
      setSecret(result.secret);
      toast.success("Signing secret rotated.");
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The signing secret could not be rotated."));
    } finally {
      setBusy("");
    }
  };

  const test = async item => {
    setBusy(item.id);
    try {
      await testSurveyWebhook(item.id);
      toast.success("Test event queued.");
    } catch (requestError) {
      setError(messageFromError(requestError, "The test event could not be queued."));
    } finally {
      setBusy("");
    }
  };

  const openDeliveries = async item => {
    setBusy(item.id);
    try {
      const result = await listSurveyWebhookDeliveries(item.id);
      setDeliveryWebhook(item);
      setDeliveries(result.items || []);
    } catch (requestError) {
      setError(messageFromError(requestError, "Webhook delivery history could not be loaded."));
    } finally {
      setBusy("");
    }
  };

  const replay = async delivery => {
    setBusy(delivery.id);
    try {
      await replaySurveyWebhookDelivery(deliveryWebhook.id, delivery.id);
      toast.success("Webhook delivery replay queued.");
      const result = await listSurveyWebhookDeliveries(deliveryWebhook.id);
      setDeliveries(result.items || []);
    } catch (requestError) {
      setError(messageFromError(requestError, "The delivery could not be replayed."));
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <section className="fera-panel mt-4" aria-labelledby="webhook-settings-heading">
        <div className="fera-panel-heading">
          <div>
            <p className="fera-eyebrow">Integrations</p>
            <h2 id="webhook-settings-heading">Customer webhooks</h2>
          </div>
          {entitled ? (
            <Button onClick={() => setForm({ ...EMPTY })}>Add webhook</Button>
          ) : <PlanLock>Paid feature</PlanLock>}
        </div>
        <ErrorAlert error={error} onRetry={load} />
        {!entitled ? (
          <Alert variant="info" className="mb-0">
            Signed survey and response events are available on Starter, Complete, and Beyond. Existing delivery history remains visible after a downgrade.
          </Alert>
        ) : null}
        {items === null ? <p className="text-muted">Loading webhooks…</p> : null}
        {items !== null && !items.length ? (
          <EmptyState
            title="No customer webhooks"
            detail={entitled
              ? "Add an HTTPS endpoint, choose events, and verify it with a signed test delivery."
              : "Upgrade to deliver signed Survey events to your own endpoint."}
            icon="link"
          />
        ) : null}
        {items?.length ? (
          <div className="table-responsive">
            <Table className="fera-table" hover>
              <thead><tr><th>Webhook</th><th>Status</th><th>Events</th><th>Secret</th><th><span className="visually-hidden">Actions</span></th></tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td><strong>{item.name}</strong><small className="d-block text-muted">{item.url}</small></td>
                    <td><StatusBadge status={item.enabled ? "active" : "disabled"} /></td>
                    <td>{item.events.length}</td>
                    <td><code>{item.secretPrefix}…</code></td>
                    <td className="text-end">
                      <div className="fera-row-actions">
                        <Button size="sm" variant="outline-primary" disabled={busy === item.id} onClick={() => openDeliveries(item)}>Deliveries</Button>
                        {entitled ? (
                          <>
                            <Button size="sm" variant="outline-secondary" disabled={busy === item.id} onClick={() => test(item)}>Test</Button>
                            <Button size="sm" variant="outline-secondary" disabled={busy === item.id} onClick={() => setForm({ ...item })}>Edit</Button>
                            <Button size="sm" variant="outline-secondary" disabled={busy === item.id} onClick={() => toggle(item)}>{item.enabled ? "Disable" : "Enable"}</Button>
                            <Button size="sm" variant="outline-secondary" disabled={busy === item.id} onClick={() => setConfirmation({ action: "rotate", item })}>Rotate</Button>
                          </>
                        ) : null}
                        <Button size="sm" variant="outline-danger" disabled={busy === item.id} onClick={() => setConfirmation({ action: "delete", item })}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </section>
      {!entitled ? <UpgradeGrid access={usage?.access} title="Add signed customer webhooks" /> : null}

      <Modal
        show={Boolean(confirmation)}
        onHide={() => setConfirmation(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmation?.action === "rotate" ? "Rotate signing secret?" : "Delete webhook?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmation?.action === "rotate"
            ? "The previous secret will stop working immediately. Update the receiving service with the new one-time secret."
            : `Delete ${confirmation?.item?.name || "this webhook"}? Delivery history will be preserved.`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmation(null)}>
            Cancel
          </Button>
          <Button
            variant={confirmation?.action === "delete" ? "danger" : "primary"}
            onClick={() => (
              confirmation?.action === "rotate"
                ? rotate(confirmation.item)
                : remove(confirmation.item)
            )}
          >
            {confirmation?.action === "rotate" ? "Rotate secret" : "Delete webhook"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(form)} onHide={() => !busy && setForm(null)} size="lg" centered>
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title>{form?.id ? "Edit webhook" : "Add webhook"}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group controlId="webhook-name">
              <Form.Label>Name</Form.Label>
              <Form.Control required maxLength={100} value={form?.name || ""} onChange={event => setForm({ ...form, name: event.target.value })} />
            </Form.Group>
            <Form.Group className="mt-3" controlId="webhook-url">
              <Form.Label>HTTPS endpoint</Form.Label>
              <Form.Control required type="url" maxLength={1024} placeholder="https://example.com/hooks/ferasurvey" value={form?.url || ""} onChange={event => setForm({ ...form, url: event.target.value })} />
              <Form.Text>Private, local, metadata-service, and redirecting destinations are blocked.</Form.Text>
            </Form.Group>
            <fieldset className="fera-webhook-events mt-3">
              <legend>Events</legend>
              {events.map(value => (
                <Form.Check
                  key={value}
                  id={`webhook-event-${value}`}
                  type="checkbox"
                  label={eventLabel(value)}
                  checked={form?.events?.includes(value) || false}
                  onChange={event => setForm({
                    ...form,
                    events: event.target.checked
                      ? [...(form.events || []), value]
                      : (form.events || []).filter(item => item !== value),
                  })}
                />
              ))}
            </fieldset>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" onClick={() => setForm(null)}>Cancel</Button>
            <Button type="submit" disabled={busy === "save" || !form?.events?.length}>{busy === "save" ? "Saving…" : "Save webhook"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(secret)} onHide={() => setSecret("")} centered>
        <Modal.Header closeButton><Modal.Title>Copy signing secret</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning">This secret is shown only once. Store it in your secret manager before closing.</Alert>
          <code className="fera-secret-value">{secret}</code>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={async () => {
            await navigator.clipboard.writeText(secret);
            toast.success("Signing secret copied.");
          }}>Copy secret</Button>
          <Button onClick={() => setSecret("")}>Done</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(deliveryWebhook)} onHide={() => setDeliveryWebhook(null)} size="xl" centered>
        <Modal.Header closeButton><Modal.Title>{deliveryWebhook?.name} deliveries</Modal.Title></Modal.Header>
        <Modal.Body>
          {!deliveries.length ? <p className="text-muted mb-0">No deliveries have been queued for this endpoint.</p> : (
            <div className="table-responsive">
              <Table className="fera-table" hover>
                <thead><tr><th>Event</th><th>Status</th><th>Attempts</th><th>HTTP</th><th>Last attempt</th><th><span className="visually-hidden">Actions</span></th></tr></thead>
                <tbody>
                  {deliveries.map(delivery => (
                    <tr key={delivery.id}>
                      <td>{eventLabel(delivery.event)}</td>
                      <td><StatusBadge status={delivery.status} /></td>
                      <td>{delivery.attempts}</td>
                      <td>{delivery.responseStatus || "—"}</td>
                      <td>{formatDate(delivery.lastAttemptAt || delivery.createdAt)}</td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-primary" disabled={busy === delivery.id} onClick={() => replay(delivery)}>Replay</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" onClick={() => setDeliveryWebhook(null)}>Close</Button></Modal.Footer>
      </Modal>
    </>
  );
}
